import { defineStore } from "pinia"
import { useRiskStore } from "@/store/index"

export const useHedgeOSStore = defineStore("hedgeOS", {
  state: () => ({
    riskStore: useRiskStore(),
    hedgeData: [],
    cashFlowData: [],
    tabs: [
      { id: "cfo", group: "CORPORATE", title: "CFO", subtitle: "Mid-market treasury · USD & EUR exposure" },
      { id: "agri", group: "PRODUCERS", title: "Agribusiness", subtitle: "Soy · Corn · Aluminum · Multi-origin" },
      { id: "desk", group: "SELL-SIDE", title: "Derivatives Desk", subtitle: "Corporate coverage · Book & client P&L" },
    ],
    operationFilters: ["ALL · 24", "IMPORTS · 11", "EXPORTS · 13", "FX ONLY", "FX + COMMODITY", "UNHEDGED", "< 30 DAYS"],
    layoutContent: {
      filterLabel: "FILTER",
      operationDetails: {
        operation: {
          title: "OPERATION DETAILS",
          settlementPrefix: "Settlement",
        },
        risk: {
          title: "RISK FACTOR DECOMPOSITION",
          description: "Live curves · exposure marked in BRL",
        },
        hedges: {
          title: "LINKED HEDGES",
          description: "Aggregate mark-to-market",
        },
      },
    },
    operationHeaders: [
      { text: "ID", value: "id", sortable: false, width: 90 },
      { text: "OPERATION", value: "operation", sortable: false },
      { text: "RISK FACTORS", value: "risk", sortable: false },
      { text: "NOTIONAL", value: "notional", sortable: false, align: "end" },
      { text: "MTM HEDGE (R$)", value: "mtm", sortable: false, align: "end" },
      { text: "COVERAGE", value: "coverage", sortable: false },
      { text: "SETTLES", value: "settles", sortable: false },
      { text: "STATUS", value: "status", sortable: false },
    ],
    operations: [
      { id: "IMP-0089", direction: "IMPORT", name: "Aluminum bars · Italy", meta: "1,000 t · supplier Tradex Srl · INV-2026-441", risk: "FX + LME", notional: "USD 2.42M", converted: "R$ 14.1M @ deal", mtm: "+ 218,000", coverage: [{ label: "USD", value: 30 }, { label: "LME", value: 52 }], settles: "18 jul", days: 73, status: "PARCIAL · ECON.", statusClass: "partial" },
      { id: "EXP-0212", direction: "EXPORT", name: "Soy · China", meta: "2,500 t · COFCO · contract CN-2026-118", risk: "FX + CBOT", notional: "USD 1.31M", converted: "R$ 7.65M @ deal", mtm: "+ 412,000", coverage: [{ label: "USD", value: 100 }, { label: "CBOT", value: 85 }], settles: "22 ago", days: 109, status: "DESIGNADO · CF", statusClass: "hedged" },
      { id: "IMP-0091", direction: "IMPORT", name: "CNC Machinery · Germany", meta: "1 unit · Trumpf · PO-2026-076", risk: "EUR", notional: "EUR 3.20M", converted: "R$ 19.74M @ deal", mtm: "+ 86,000", coverage: [{ label: "EUR", value: 88 }], settles: "30 jun", days: 67, status: "DESIGNADO · CF", statusClass: "hedged" },
      { id: "EXP-0205", direction: "EXPORT", name: "Auto parts · USA", meta: "12,400 units · ACME Corp · LC-2026-088", risk: "USD", notional: "USD 1.84M", converted: "R$ 10.71M @ deal", mtm: "+ 158,000", coverage: [{ label: "USD", value: 75 }], settles: "15 mai", days: 21, status: "DESIGNADO · CF", statusClass: "hedged" },
      { id: "EXP-0218", direction: "EXPORT", name: "Aluminum sheets · Italy", meta: "200 t · Bormioli SpA · IT-2026-031", risk: "FX + LME", notional: "USD 484k", converted: "R$ 2.82M @ deal", mtm: "+ 38,000", coverage: [{ label: "USD", value: 60 }, { label: "LME", value: 0 }], settles: "10 jun", days: 47, status: "PARCIAL · ECON.", statusClass: "partial" },
      { id: "EXP-0142", direction: "EXPORT", name: "Specialty chemicals · Italy", meta: "12 t · Riedel · contract IT-2026-009", risk: "EUR", notional: "EUR 1.40M", converted: "R$ 8.64M @ deal", mtm: "—", coverage: [{ label: "EUR", value: 0 }], settles: "30 jun", days: 67, status: "NÃO HEDGEADA", statusClass: "open" },
    ],
    loading: false,
    error: null,

    // ---- Page/UI state (single source of truth for HedgeOSView) ----
    activePersona: "cfo",
    activeFilter: "ALL · 24",
    expanded: null,
    ticketOpen: false,
    newOperationOpen: false,
    toast: "",
    clock: "14:32:07",
    quotes: [
      { symbol: "USD/BRL", value: "5.8342", move: "↑ 0.42%", up: true },
      { symbol: "EUR/BRL", value: "6.3120", move: "↑ 0.31%", up: true },
      { symbol: "SOY·CBOT", value: "1048.75", move: "↓ 0.88%" },
      { symbol: "CORN·CBOT", value: "442.50", move: "↓ 0.22%" },
    ],
    tabContent: {
      cfo: {
        title: 'Every operation.<strong>Every <em>risk factor</em>.</strong>',
        desc: 'An import or export contract is the unit of risk — not a currency bucket, not a commodity ticker. hedgeOS tracks each operation, every risk factor riding on it, and the hedges placed against it. Live MtM. One source of truth.',
        grid: {
          liveOperations: {
            label: 'LIVE OPERATIONS',
            value: '24',
            note: '11 import · 13 export · USD + EUR + COMMODITY'
          },
          totalNotional: {
            label: 'TOTAL NOTIONAL',
            value: 'R$ 312<sup>M</sup>',
            note: 'Equiv. at deal-date FX · 90d horizon'
          },
          aggregateMtm: {
            label: 'AGGREGATE HEDGE MTM',
            value: '+ R$ 3.42<sup>M</sup>',
            note: '↑ R$ 182k since open · all hedges',
            tone: 'positive'
          },
          netOpenRisk: {
            label: 'NET OPEN RISK',
            value: 'R$ 38.6<sup>M</sup>',
            note: '12.4% of notional · target ≤ 15%',
            tone: 'negative'
          }
        },
        signal: {
          label: '● SIGNAL · 14:31',
          text: 'Operation <em>EXP-0142</em> (export to Italy, EUR 1.4M, 67d) is <strong>not yet hedged</strong>. EUR/BRL forward at <strong>6.294</strong> would lock <strong>R$ 8.81M</strong>. Operation <em>IMP-0089</em> needs both legs — pair NDF USD buy with LME long.',
          action: 'BUILD HEDGE →'
        },
        section: {
          title: 'OPERATIONS LEDGER',
          meta: 'IMPORT · EXPORT · LIVE MTM PER OPERATION',
          type: 'ledger',
          action: '+ NEW OPERATION'
        }
      },
      agri: {
        title: 'From <em>farm-gate</em> to CBOT,<strong>one margin.</strong>',
        desc: "Physical harvest, port basis, FX conversion and CBOT hedge — all reconciled in R$/saca real-time. See exactly what you're locking in, not just what the screen says.",
        grid: {
          physicalInventory: {
            label: 'PHYSICAL INVENTORY',
            value: '42,500 <sup>t</sup>',
            note: 'Soy · Sorriso/MT · Ready to ship'
          },
          lockedMargin: {
            label: 'LOCKED-IN MARGIN',
            value: 'R$ 14.80<sup>/sc</sup>',
            note: '↑ vs R$ 9.20/sc breakeven',
            tone: 'positive'
          },
          hedgeCoverage: {
            label: 'HEDGE COVERAGE',
            value: '72%',
            note: '380 contracts CBOT · SELL'
          },
          portBasis: {
            label: 'PORT BASIS · PARANAGUÁ',
            value: '+82<sup>c/bu</sup>',
            note: 'vs 5-yr avg +65 · bullish'
          }
        },
        signal: {
          label: '● SIGNAL · 14:29',
          text: 'With the port premium at <em>+82c/bu</em> and CBOT Jul at <strong>1048.75</strong>, locking <strong>28% open tonnage</strong> now yields <strong>R$ 16.40/sc</strong> — above your target of R$ 15.00. FX component: NDF USD sell @ <strong>5.812</strong>.',
          action: 'STAGE ORDER →'
        },
        section: {
          title: 'PHYSICAL BOOK · LIVE MARGIN',
          meta: 'ORIGIN · BASIS · FX · CBOT · COVERAGE',
          type: 'cards',
          cards: [
            { eyebrow: 'SOY · SORRISO/MT', title: 'Safra 2026 · 18,500 t', status: '72% HEDGED', tone: 'positive', metrics: [{ label: 'Basis', value: '+82 c/bu' }, { label: 'CBOT', value: '1048.75' }, { label: 'FX', value: '5.8342' }, { label: 'Margin', value: 'R$ 14.80/sc', tone: 'positive' }] },
            { eyebrow: 'SOY · RIO VERDE/GO', title: 'Safra 2026 · 12,000 t', status: '58% HEDGED', tone: 'warning', metrics: [{ label: 'Basis', value: '+67 c/bu' }, { label: 'CBOT', value: '1048.75' }, { label: 'FX', value: '5.8342' }, { label: 'Margin', value: 'R$ 12.30/sc', tone: 'positive' }] },
            { eyebrow: 'CORN · SINOP/MT', title: '2ª Safra · 8,400 t', status: '41% HEDGED', tone: 'negative', metrics: [{ label: 'Basis', value: '-18 c/bu' }, { label: 'CBOT', value: '442.50' }, { label: 'FX', value: '5.8342' }, { label: 'Margin', value: 'R$ 7.90/sc', tone: 'warning' }] },
            { eyebrow: 'ALUMINUM · PARANAGUÁ', title: 'Physical · 3,600 t', status: '84% HEDGED', tone: 'positive', metrics: [{ label: 'LME 3M', value: 'USD 2,418' }, { label: 'Premium', value: '+USD 118' }, { label: 'FX', value: '5.8342' }, { label: 'Margin', value: 'R$ 820/t', tone: 'positive' }] }
          ]
        }
      },
      desk: {
        title: '<em>Every</em> corporate book.<strong>One pane of glass.</strong>',
        desc: "Real-time client-level exposure and MtM across your corporate coverage. Spot under-hedged clients before they call. Spot over-hedged clients before the auditor does. Price on the curve that actually clears.",
        grid: {
          bookNotional: {
            label: 'BOOK NOTIONAL',
            value: 'R$ 2.41<sup>Bn</sup>',
            note: '184 clients · 1,247 live contracts'
          },
          netDelta: {
            label: 'NET DELTA · FX',
            value: 'USD -38.4<sup>M</sup>',
            note: 'to be squared via DOL futuro',
            tone: 'negative'
          },
          bookMtm: {
            label: 'BOOK MTM (T+0)',
            value: '+ R$ 8.92<sup>M</sup>',
            note: 'Client P&L · pre-spread',
            tone: 'positive'
          },
          marginCalls: {
            label: 'MARGIN CALLS TODAY',
            value: '3',
            note: '● 2 pending · 1 resolved',
            toneNote: 'warning'
          }
        },
        signal: {
          label: '● ALERT · 14:30',
          text: '<em>Agrofoods SA</em> approaching 85% credit line usage. Next NDF roll would breach. Meanwhile, <em>Minerva-Style Co.</em> has EUR 8M receivable coming due in 12d — high probability of a new NDF sale request at <strong>6.30–6.35</strong>.',
          action: 'OPEN ACCOUNTS →'
        },
        section: {
          title: 'CORPORATE COVERAGE · LIVE',
          meta: 'STATUS · EXPOSURE · UTILIZATION',
          type: 'client-cards',
          cards: [
            { eyebrow: 'SOY EXPORTER · MT', title: 'Agrofoods SA', status: 'ACTION REQUIRED', tone: 'negative', metrics: [{ label: 'Notional', value: 'USD 84M' }, { label: 'Credit line', value: '85%', tone: 'negative' }, { label: 'MtM', value: '+R$ 1.2M', tone: 'positive' }, { label: 'Next call', value: '12 days' }] },
            { eyebrow: 'OIL & GAS · RJ', title: 'PetroMid Ltda', status: 'HEALTHY', tone: 'positive', metrics: [{ label: 'Notional', value: 'USD 210M' }, { label: 'Credit line', value: '42%' }, { label: 'MtM', value: '+R$ 3.8M', tone: 'positive' }, { label: 'Next call', value: '45 days' }] },
            { eyebrow: 'MEAT PROCESSOR · SP', title: 'Minerva-Style Co.', status: 'WATCH', tone: 'warning', metrics: [{ label: 'Notional', value: 'USD 140M' }, { label: 'Credit line', value: '68%', tone: 'warning' }, { label: 'MtM', value: '+R$ 2.1M', tone: 'positive' }, { label: 'Next call', value: '12 days' }] },
            { eyebrow: 'CEMENT · RS', title: 'CementaSul', status: 'HEALTHY', tone: 'positive', metrics: [{ label: 'Notional', value: 'EUR 32M' }, { label: 'Credit line', value: '28%' }, { label: 'MtM', value: '-R$ 180k', tone: 'negative' }, { label: 'Next call', value: '78 days' }] },
            { eyebrow: 'AUTOMOTIVE · MG', title: 'AutoParts BR', status: 'HEALTHY', tone: 'positive', metrics: [{ label: 'Notional', value: 'USD 55M' }, { label: 'Credit line', value: '51%' }, { label: 'MtM', value: '+R$ 640k', tone: 'positive' }, { label: 'Next call', value: '25 days' }] },
            { eyebrow: 'ELECTRONICS · SP', title: 'TechImport Ltda', status: 'WATCH', tone: 'warning', metrics: [{ label: 'Notional', value: 'USD 18M' }, { label: 'Credit line', value: '72%' }, { label: 'MtM', value: '-R$ 220k', tone: 'negative' }, { label: 'Next call', value: '7 days' }] },
            { eyebrow: 'METALS · MG', title: 'AluCorp BR', status: 'HEALTHY', tone: 'positive', metrics: [{ label: 'Notional', value: 'USD 88M' }, { label: 'Credit line', value: '38%' }, { label: 'MtM', value: '+R$ 1.4M', tone: 'positive' }, { label: 'Next call', value: '34 days' }] },
            { eyebrow: 'PHARMACEUTICALS · SP', title: 'Pharma Global BR', status: 'HEALTHY', tone: 'positive', metrics: [{ label: 'Notional', value: 'EUR 48M' }, { label: 'Credit line', value: '45%' }, { label: 'MtM', value: '+R$ 820k', tone: 'positive' }, { label: 'Next call', value: '52 days' }] }
          ]
        }
      },
    },
  }),
  getters: {
    // ---- Exposure tab (receivables / payables ladder) ----
    maturitiesLadder(state) {
      return state.cashFlowData.map((row) => {
        const amount = Number(row.Amount) || 0
        return {
          date: row.Date,
          formattedDate: (() => {
            const [year, month, day] = String(row.Date).split("-")
            return year && month && day ? `${day}/${month}/${year}` : row.Date
          })(),
          description: row.Description,
          ccy: row.Currency,
          receivable: amount > 0 ? amount : "-",
          payable: amount < 0 ? Math.abs(amount) : "-",
        }
      })
    },
    totalReceivables(state) {
      return state.cashFlowData.reduce((sum, row) => {
        const amount = Number(row.Amount) || 0
        return amount > 0 ? sum + amount : sum
      }, 0)
    },
    totalPayables(state) {
      return state.cashFlowData.reduce((sum, row) => {
        const amount = Number(row.Amount) || 0
        return amount < 0 ? sum + Math.abs(amount) : sum
      }, 0)
    },
    netFxExposure() {
      return this.totalReceivables - this.totalPayables
    },
    exposureVar() {
      // 95% VaR approximation: 1.65 standard deviations of the daily net cash-flow amounts.
      const amounts = this.cashFlowData.map((row) => Number(row.Amount) || 0)
      return 1.65 * standardDeviation(amounts)
    },

    // ---- Hedge tab (NDF derivatives) ----
    totalNdfSellVolume(state) {
      return state.hedgeData.reduce((sum, row) => sum + (Number(row.ndf_sell_volume) || 0), 0)
    },
    totalNdfBuyVolume(state) {
      return state.hedgeData.reduce((sum, row) => sum + (Number(row.ndf_buy_volume) || 0), 0)
    },
    totalDerivativesVolume() {
      return this.totalNdfSellVolume + this.totalNdfBuyVolume
    },
    netExposureAfterHedge(state) {
      const last = state.hedgeData[state.hedgeData.length - 1]
      return last ? Number(last.net_exposure_after_hedge) || 0 : 0
    },
    maxHedgeRatio() {
      if (!this.netFxExposure) return 0
      return Math.min(100, (this.totalDerivativesVolume / Math.abs(this.netFxExposure)) * 100)
    },
    hedgeVar(state) {
      const series = state.hedgeData.map((row) => Number(row.net_exposure_after_hedge) || 0)
      return 1.65 * standardDeviation(series)
    },
    hedgeChartDates(state) {
      return state.hedgeData.map((row) => row.date)
    },

    // ---- HedgeOS page (persona + operations ledger) ----
    visibleOperations(state) {
      const operations = state.operations
      if (state.activeFilter.startsWith("IMPORT")) return operations.filter((o) => o.direction === "IMPORT")
      if (state.activeFilter.startsWith("EXPORT")) return operations.filter((o) => o.direction === "EXPORT")
      if (state.activeFilter === "UNHEDGED") return operations.filter((o) => o.statusClass === "open")
      if (state.activeFilter === "< 30 DAYS") return operations.filter((o) => o.days < 30)
      if (state.activeFilter === "FX ONLY") return operations.filter((o) => !o.risk.includes("+"))
      if (state.activeFilter === "FX + COMMODITY") return operations.filter((o) => o.risk.includes("+"))
      return operations
    },
    activeTabContent(state) {
      return state.tabContent[state.activePersona] || state.tabContent.desk
    },
  },
  actions: {
    async fetchHedgeData() {
      const data = await this.riskStore.getExposureData(false)
      this.hedgeData = data?.data || []
    },
    async fetchCashFlowData() {
      const data = await this.riskStore.getExposureData(true)
      this.cashFlowData = data?.data || []
    },
    async fetchAll() {
      this.loading = true
      this.error = null
      try {
        await Promise.all([this.fetchHedgeData(), this.fetchCashFlowData()])
      } catch (error) {
        this.error = error
      } finally {
        this.loading = false
      }
    },

    // ---- HedgeOS page (persona + operations ledger) ----
    setActivePersona(id) {
      this.activePersona = id
    },
    setActiveFilter(value) {
      this.activeFilter = value
    },
    toggleOperation(id) {
      this.expanded = this.expanded === id ? null : id
    },
    openNewOperation() {
      this.newOperationOpen = true
    },
    handleSignalAction() {
      if (this.activePersona === "cfo") this.ticketOpen = true
    },
    closeDrawer() {
      this.ticketOpen = false
      this.newOperationOpen = false
    },
    completeAction(hedgeMessage, operationMessage) {
      const message = this.ticketOpen ? hedgeMessage : operationMessage
      this.closeDrawer()
      this.toast = message
      setTimeout(() => {
        this.toast = ""
      }, 3500)
    },
  },
})

function standardDeviation(values) {
  if (!values.length) return 0
  const mean = values.reduce((sum, v) => sum + v, 0) / values.length
  const variance = values.reduce((sum, v) => sum + (v - mean) ** 2, 0) / values.length
  return Math.sqrt(variance)
}
